const pending = new WeakMap();
const initialized = new WeakSet();

/** Activates trusted HTML templates on interaction or visibility. */
export default class DeferredContent {
    static init(scope = document) {
        const doc = scope.ownerDocument || scope;
        if (!initialized.has(doc)) {
            initialized.add(doc);
            doc.addEventListener('click', event => {
                const trigger = event.target.closest?.('[data-wi-deferred-trigger]');
                if (!trigger || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
                const root = trigger.closest('[data-wi-deferred]');
                if (!root || root.dataset.wiDeferredState === 'error') return;
                event.preventDefault();
                this.load(root, { focus: true }).catch(() => {});
            });
            doc.addEventListener('keydown', event => {
                if (event.key === ' ' && event.target.matches('a[data-wi-deferred-trigger][role="button"]')) {
                    event.preventDefault();
                    event.target.click();
                }
            });
            const mutations = new MutationObserver(records => {
                for (const record of records) {
                    for (const node of record.addedNodes) {
                        if (node.nodeType === 1) this.init(node);
                    }
                }
            });
            mutations.observe(doc.documentElement, { childList: true, subtree: true });
        }
        const roots = [...scope.querySelectorAll('[data-wi-deferred]')];
        if (scope.matches?.('[data-wi-deferred]')) roots.unshift(scope);
        for (const root of roots) {
            if (initialized.has(root)) continue;
            initialized.add(root);
            root.querySelectorAll('a[data-wi-deferred-trigger]').forEach(link => link.setAttribute('role', 'button'));
            if (root.dataset.wiDeferred !== 'visible' || typeof IntersectionObserver === 'undefined') continue;
            const observer = new IntersectionObserver(entries => {
                if (!entries.some(entry => entry.isIntersecting)) return;
                observer.disconnect();
                this.load(root).catch(() => {});
            });
            observer.observe(root);
        }
    }

    static load(target, { focus = false } = {}) {
        const root = typeof target === 'string' ? document.querySelector(target) : target;
        if (!root) return Promise.reject(new Error('Deferred content not found.'));
        if (pending.has(root)) return pending.get(root);
        const task = this.activate(root, focus);
        pending.set(root, task);
        return task;
    }

    static async activate(root, focus) {
        const template = root.querySelector(':scope > template[data-wi-deferred-template]');
        if (!template) throw new Error('Deferred template not found.');
        const doc = root.ownerDocument;
        const content = doc.createElement('div');
        content.className = 'wi-deferred-content';
        content.append(template.content.cloneNode(true));
        const scripts = [...content.querySelectorAll('script')];
        scripts.forEach(script => script.remove());
        root.dataset.wiDeferredState = 'loading';
        root.setAttribute('aria-busy', 'true');
        root.append(content);
        try {
            // Sequential activation preserves dependencies between trusted scripts.
            for (const original of scripts) {
                await new Promise((resolve, reject) => {
                    const script = doc.createElement('script');
                    for (const attribute of original.attributes) script.setAttribute(attribute.name, attribute.value);
                    const asynchronous = original.src || original.type === 'module';
                    if (asynchronous) {
                        script.async = false;
                        script.onload = resolve;
                        script.onerror = () => reject(new Error('Deferred script failed to load.'));
                    }
                    script.textContent = original.textContent;
                    content.append(script);
                    if (!asynchronous) resolve();
                });
            }
            root.querySelector(':scope > [data-wi-deferred-placeholder]')?.remove();
            template.remove();
            root.dataset.wiDeferredState = 'ready';
            if (focus) {
                const target = content.querySelector('iframe, button, a[href], [tabindex]') || content;
                if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
                target.focus({ preventScroll: true });
            }
            root.dispatchEvent(new CustomEvent('wi:deferred:ready', { bubbles: true }));
            return content;
        } catch (error) {
            content.remove();
            root.dataset.wiDeferredState = 'error';
            root.querySelectorAll('[data-wi-deferred-trigger]').forEach(trigger => {
                trigger.removeAttribute('role');
                if (trigger.tagName === 'BUTTON') trigger.disabled = true;
            });
            root.dispatchEvent(new CustomEvent('wi:deferred:error', { bubbles: true, detail: { error } }));
            throw error;
        } finally {
            root.removeAttribute('aria-busy');
        }
    }
}
